import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDate } from '../utils/dateUtils';
import {
  Users,
  UserCheck,
  Calendar,
  TrendingUp,
  AlertTriangle,
  FileText,
  IndianRupee,
  BarChart3,
  Download,
  Settings,
  Bell,
  Search,
  Crown,
  Building2,
  Target,
  LogOut,
  User,
  CreditCard,
  CheckSquare,
  Home,
  Clock,
  CheckCircle,
  XCircle,
  Shield,
  Receipt,
  BookOpen,
  Landmark
} from 'lucide-react';

// Import loan and payroll components
import { PayrollManagement } from './payroll';
import LoansAdvancesManagement from './admin/LoansAdvancesManagement';
import EmployeeManagement from './admin/EmployeeManagement';
import Reports from './admin/Reports';
import AttendanceManagement from './admin/AttendanceManagement';
import LeaveManagement from './admin/LeaveManagement';
import ShiftRoster from './dashboards/ShiftRoster';
import PayrollGovernanceCenter from './admin/PayrollGovernanceCenter';
import PromotionIncrementManagement from './admin/PromotionIncrementManagement';
import SuspensionManagement from './admin/SuspensionManagement';
import ReimbursementReviewPage from './reimbursements/ReimbursementReviewPage';
import ArrearManagement from './admin/ArrearManagement';
import ProvidentFund from './providentfund/ProvidentFund';
import IncomeTaxConfiguration from './admin/IncomeTaxConfiguration';
import FestivalAdvanceManagement from './admin/FestivalAdvanceManagement';
import DemoArrearManagement from './admin/DemoArrearManagement';
import BankLoanManagement from './admin/BankLoanManagement';
import LICManagement from './admin/LICManagement';
import PensionPayrollManagement from './pension-payroll/PensionPayrollManagement';
import { buildApiUrl } from '../utils/apiSetup';

// Sample data based on the design documents
const sampleData = {
  totalEmployees: 350,
  permanentEmployees: 200,
  contractualEmployees: 150,
  presentToday: 325,
  leavesPending: 12,
  loansPending: 4,
  exitsUpcoming: 7,
  lastPayrollProcessed: 'July 31, 2025',
  thisMonthPayroll: 'Pending',
  payrollHistory: [
    { month: 'Feb', amount: 2800000 },
    { month: 'Mar', amount: 2950000 },
    { month: 'Apr', amount: 3100000 },
    { month: 'May', amount: 3050000 },
    { month: 'Jun', amount: 3200000 },
    { month: 'Jul', amount: 3150000 }
  ],
  leaveUtilization: [
    { type: 'EL', used: 450, total: 600 },
    { type: 'PL', used: 120, total: 200 },
    { type: 'CL', used: 80, total: 150 }
  ],
  loanSummary: {
    totalDisbursed: 1850000,
    emiCollected: 1200000,
    pendingEMI: 650000
  }
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

const Dashboard = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const activeTab = searchParams.get('tab') || 'dashboard';

  const setActiveTab = (tabId, replace = false) => {
    const nextParams = new URLSearchParams(searchParams);
    if (!tabId || tabId === 'dashboard') {
      nextParams.delete('tab');
    } else {
      nextParams.set('tab', tabId);
    }
    setSearchParams(nextParams, { replace });
  };

  // Real metrics state
  const [metrics, setMetrics] = useState({
    totalEmployees: 0,
    activeLoans: 0,
    totalOutstanding: 0,
    previousMonthNetPay: 0,
    approvedPayrolls: 0,
    latestPayrollMonth: null
  });
  const [pensionerStats, setPensionerStats] = useState({
    total_pensioners: 0,
    active_pensioners: 0,
    stopped_pensioners: 0,
    family_pension: 0,
    retiring_this_month: 0
  });

  useEffect(() => {
    setIsLoaded(true);
    fetchDashboardMetrics();
  }, []);

  useEffect(() => {
    const handleNavigate = (e) => {
      if (e.detail && e.detail.tab) {
        setActiveTab(e.detail.tab);
        if (e.detail.subTab) {
          localStorage.setItem('active_leave_subtab', e.detail.subTab);
        }
      }
    };
    window.addEventListener('navigate-to-tab', handleNavigate);
    return () => window.removeEventListener('navigate-to-tab', handleNavigate);
  }, [searchParams]);

  // Fetch real dashboard metrics
  const fetchDashboardMetrics = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch employee count
      const empResponse = await fetch(buildApiUrl('/api/v1/benfish-employees/search?query=&limit=1'), { headers });
      if (empResponse.ok) {
        const empData = await empResponse.json();
        setMetrics(prev => ({ ...prev, totalEmployees: empData.total_count || 0 }));
      }

      // Fetch loan portfolio stats
      const loanResponse = await fetch(buildApiUrl('/api/v1/benfish-loans/portfolio/stats'), { headers });
      if (loanResponse.ok) {
        const loanData = await loanResponse.json();
        setMetrics(prev => ({
          ...prev,
          activeLoans: loanData.active_loans || 0,
          totalOutstanding: loanData.total_outstanding || 0
        }));
      }

      // Fetch payroll runs
      const payrollResponse = await fetch(buildApiUrl('/api/v1/benfish-payroll/runs'), { headers });
      if (payrollResponse.ok) {
        const payrollData = await payrollResponse.json();
        const approvedRuns = payrollData.filter(r => r.status === 'approved');

        // Calculate previous month (current month - 1)
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1; // Convert to 1-indexed (1 = January)
        const currentYear = currentDate.getFullYear();

        // Calculate previous month
        const targetMonth = currentMonth === 1 ? 12 : currentMonth - 1;
        const targetYear = currentMonth === 1 ? currentYear - 1 : currentYear;

        // Find the payroll run for the previous month
        const previousMonthPayroll = approvedRuns.find(r => 
          r.payroll_month === targetMonth && r.payroll_year === targetYear
        );

        // If previous month payroll not found, use the latest approved as fallback
        const fallbackPayroll = approvedRuns.sort((a, b) =>
          (b.payroll_year * 12 + b.payroll_month) - (a.payroll_year * 12 + a.payroll_month)
        )[0];

        const selectedPayroll = previousMonthPayroll || fallbackPayroll;

        if (selectedPayroll) {
          // Use backend's total_net_pay to ensure consistency with payroll editor
          const netPay = selectedPayroll.total_net_pay || 0;
          setMetrics(prev => ({
            ...prev,
            approvedPayrolls: approvedRuns.length,
            latestPayrollMonth: `${new Date(2025, selectedPayroll.payroll_month - 1).toLocaleString('default', { month: 'short' })} ${selectedPayroll.payroll_year}`,
            previousMonthNetPay: netPay
          }));
        } else {
          setMetrics(prev => ({
            ...prev,
            approvedPayrolls: approvedRuns.length,
            latestPayrollMonth: null,
            previousMonthNetPay: 0
          }));
        }
      }

      const pensionerResponse = await fetch(buildApiUrl('/api/v1/pensioners/stats'), { headers });
      if (pensionerResponse.ok) {
        const pensionerData = await pensionerResponse.json();
        setPensionerStats({
          total_pensioners: pensionerData.total_pensioners || 0,
          active_pensioners: pensionerData.active_pensioners || 0,
          stopped_pensioners: pensionerData.stopped_pensioners || 0,
          family_pension: pensionerData.family_pension || 0,
          retiring_this_month: pensionerData.retiring_this_month || 0
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
    }
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.user-menu')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const KPICard = ({ title, value, icon: Icon, color, trend, isPercentage = false }) => (
    <motion.div
      variants={itemVariants}
      className="bg-white rounded-xl p-6 shadow-lg card-hover border border-gray-100"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <div className="text-3xl font-bold text-gray-900">
            {isLoaded && (
              <CountUp
                end={value}
                duration={2}
                separator=","
                suffix={isPercentage ? '%' : ''}
              />
            )}
          </div>
          {trend && (
            <div className="flex items-center mt-2">
              <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
              <span className="text-xs text-green-600 font-medium">{trend}</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </motion.div>
  );

  const AlertCard = ({ title, items }) => (
    <motion.div
      variants={itemVariants}
      className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
    >
      <div className="flex items-center mb-4">
        <Bell className="w-5 h-5 text-orange-500 mr-2" />
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="space-y-3">
        {items.map((item, index) => (
          <motion.div
            key={index}
            className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border-l-4 border-amber-400"
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: index * 0.1 }}
          >
            <span className="text-sm font-medium text-amber-800">{item.text}</span>
            <span className="bg-amber-100 text-amber-800 text-xs font-bold px-2 py-1 rounded-full">
              {item.count}
            </span>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );

  const QuickActionButton = ({ icon: Icon, label, onClick, color = 'primary' }) => (
    <motion.button
      onClick={onClick}
      className={`flex flex-col items-center justify-center p-6 bg-white border-2 ${color === 'green' ? 'border-green-200 hover:border-green-500 hover:bg-green-50' :
        color === 'orange' ? 'border-orange-200 hover:border-orange-500 hover:bg-orange-50' :
          color === 'blue' ? 'border-blue-200 hover:border-blue-500 hover:bg-blue-50' :
            color === 'purple' ? 'border-purple-200 hover:border-purple-500 hover:bg-purple-50' :
              'border-primary-200 hover:border-primary-500 hover:bg-primary-50'
        } rounded-xl transition-all duration-300 group`}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <Icon className={`w-8 h-8 ${color === 'green' ? 'text-green-600' :
        color === 'orange' ? 'text-orange-600' :
          color === 'blue' ? 'text-blue-600' :
            color === 'purple' ? 'text-purple-600' :
              'text-primary-600'
        } mb-2`} />
      <span className="text-sm font-medium text-gray-700 text-center">
        {label}
      </span>
    </motion.button>
  );

  // StatCard component for metrics display
  const StatCard = ({ icon: Icon, title, value, subtitle, color, onClick }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 cursor-pointer"
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <div className="text-3xl font-bold text-gray-900">{value}</div>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </motion.div>
  );

  // Define tabs for admin dashboard dynamically based on permissions
  const allTabs = [
    { id: 'dashboard', label: 'Overview', icon: Home, requiredPermission: null },
    {
      id: 'employee-management',
      label: 'Employee Management',
      icon: Users,
      requiredPermission: ['employee:view:all', 'employee:view:dept', 'employee:view:own', 'employee:create', 'employee:update:all', 'employee:update:dept', 'employee:update:own', 'employee:delete', 'employee:import', 'employee:export']
    },
    { id: 'reports', label: 'Reports', icon: BarChart3, requiredPermission: ['report:view:all', 'report:view:dept', 'report:export'] },
    { id: 'payroll', label: 'Payroll Management', icon: IndianRupee, requiredPermission: ['payroll:view', 'payroll:generate', 'payroll:edit', 'payroll:approve', 'payroll:master:view', 'payroll:master:edit'] },
    { id: 'pension-payroll', label: 'Pension Payroll Management', icon: Landmark, requiredPermission: ['pension_payroll:view', 'pension_payroll:generate', 'pension_payroll:da_config:view', 'pension_payroll:history', 'pension_payroll:detail'] },
    //{ id: 'loans-advances', label: 'Loans & Advances', icon: CreditCard, requiredPermission: ['loan:view', 'loan:add', 'loan:approve'] },
    { id: 'it-configuration', label: 'IT Configuration', icon: Settings, requiredPermission: ['itconfig:add', 'itconfig:edit', 'itconfig:assign'] },
    { id: 'increments', label: 'Promotion & Increment Management', icon: TrendingUp, requiredPermission: ['progression:increment', 'progression:promotion', 'progression:functional_promotion'] },
    { id: 'suspensions', label: 'Suspension Management', icon: Shield, requiredPermission: ['suspension:suspend', 'suspension:revoke', 'suspension:view_history'] },
    { id: 'attendance', label: 'Attendance', icon: CheckSquare, requiredPermission: ['attendance:view:all', 'attendance:view:dept', 'attendance:view:own', 'attendance:mark:own', 'attendance:mark:dept', 'attendance:approve:dept', 'attendance:approve:all'] },
    { id: 'leave', label: 'Leave', icon: Calendar, requiredPermission: ['leave:request:view', 'leave:policy:create', 'leave:policy:edit', 'leave:policy:activate', 'leave:policy:deactivate', 'leave:policy:assign', 'leave:absence:summary', 'leave:absence:check', 'leave:arrear:view', 'leave:sheet:generate'] },
    { id: 'shift_roster', label: 'Shifts & Rosters', icon: Building2, requiredPermission: ['shift:view:all', 'shift:view:dept', 'shift:view:own', 'shift:create', 'shift:edit', 'shift:delete', 'shift:assign'] },
    //{ id: 'reimbursements', label: 'Employee Reimbursement', icon: FileText, requiredPermission: ['reimbursement:view:all', 'reimbursement:view:dept', 'reimbursement:view:own', 'reimbursement:create:own', 'reimbursement:approve:all'] },
    { id: 'provident-fund', label: 'Provident Fund', icon: Receipt, requiredPermission: ['pf:view', 'pf:add', 'pf:approve'] },
    { id: 'festival-advance', label: 'Festival Advance', icon: BookOpen, requiredPermission: ['festival_advance:view', 'festival_advance:add', 'festival_advance:approve'] },
    { id: 'bank-loan', label: 'Bank Loan', icon: Landmark, requiredPermission: ['bank:loan:view', 'bank:view', 'bank:loan:add', 'bank:loan:stop', 'bank:add', 'bank:edit'] },
    { id: 'lic', label: 'LIC', icon: Shield, requiredPermission: ['lic:view', 'lic:add', 'lic:stop'] },
    { id: 'arrears', label: 'DA Arrears', icon: Receipt, requiredPermission: ['arrear:view', 'arrear:admin'] }
  ];

  const tabs = allTabs.filter(tab => {
    if (tab.id === 'dashboard') return true;
    if (Array.isArray(tab.requiredPermission)) {
      return tab.requiredPermission.some(perm => hasPermission(perm));
    }
    return hasPermission(tab.requiredPermission);
  });

  useEffect(() => {
    if (tabs.length > 0 && activeTab !== 'dashboard' && !tabs.some(t => t.id === activeTab)) {
      setActiveTab('dashboard', true);
    }
  }, [tabs, activeTab]);

  // Placeholder component for Attendance
  const AttendancePlaceholder = () => (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center mb-4">
          <CheckSquare className="w-8 h-8 text-blue-600 mr-3" />
          <h2 className="text-2xl font-bold text-gray-800">Attendance Management</h2>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-4">Maker-Checker Pattern</h3>
          <div className="space-y-4">
            <div className="bg-white rounded-lg p-4 border-l-4 border-blue-500">
              <h4 className="font-semibold text-blue-800 mb-2">Maker Role</h4>
              <p className="text-gray-700">
                The Maker will be responsible for creating and entering daily attendance records for employees.
                This includes marking present, absent, half-day, on leave, or other attendance statuses.
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
              <h4 className="font-semibold text-green-800 mb-2">Checker Role</h4>
              <p className="text-gray-700">
                The Checker will review and approve the attendance records created by the Maker.
                This ensures accuracy and proper verification before the attendance data is finalized.
              </p>
            </div>
          </div>
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> This feature will implement a two-step approval process where attendance
              records must be verified and approved before being considered final. This helps maintain data
              accuracy and accountability.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Upcoming Features</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-800 mb-2">Daily Attendance Entry</h4>
            <p className="text-sm text-gray-600">Bulk entry interface for marking daily attendance</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-800 mb-2">Approval Queue</h4>
            <p className="text-sm text-gray-600">Review and approve pending attendance records</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-800 mb-2">Attendance Reports</h4>
            <p className="text-sm text-gray-600">Generate monthly and custom attendance reports</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-800 mb-2">Attendance History</h4>
            <p className="text-sm text-gray-600">View historical attendance data and trends</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Placeholder component for Leave
  const LeavePlaceholder = () => {
    const leaveRequests = [
      {
        id: 1,
        employee: 'Rahul Sharma',
        empCode: 'EMP101',
        department: 'Cardiology',
        leaveType: 'Casual Leave',
        from: '2026-05-28',
        to: '2026-05-30',
        days: 3,
        reason: 'Family Function',
        status: 'Pending'
      },
      {
        id: 2,
        employee: 'Priya Verma',
        empCode: 'EMP102',
        department: 'Radiology',
        leaveType: 'Sick Leave',
        from: '2026-05-26',
        to: '2026-05-27',
        days: 2,
        reason: 'Medical Rest',
        status: 'Approved'
      },
      {
        id: 3,
        employee: 'Amit Das',
        empCode: 'EMP103',
        department: 'ICU',
        leaveType: 'Earned Leave',
        from: '2026-06-02',
        to: '2026-06-05',
        days: 4,
        reason: 'Vacation',
        status: 'Rejected'
      }
    ];

    const employeesOnLeave = [
      {
        employee: 'Sneha Patil',
        department: 'Emergency',
        leaveType: 'Sick Leave'
      },
      {
        employee: 'Vikas Kumar',
        department: 'Pharmacy',
        leaveType: 'Casual Leave'
      }
    ];

    const leaveBalances = [
      {
        employee: 'Rahul Sharma',
        cl: 5,
        sl: 8,
        el: 12
      },
      {
        employee: 'Priya Verma',
        cl: 4,
        sl: 6,
        el: 15
      }
    ];

    return (
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header: Leave Management + Shift & Roster */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-center">
              <Calendar className="w-10 h-10 text-green-600 mr-4" />
              <div>
                <h2 className="text-3xl font-bold text-gray-800">Leave Management</h2>
                <p className="text-gray-500 mt-1">Manage leave requests, approvals, balances, and leave monitoring</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button className="px-5 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium shadow">
                + Apply Leave
              </button>

              <div className="bg-white border rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <Building2 className="w-8 h-8 text-primary-600" />
                  <div>
                    <p className="text-sm text-gray-500">Shift & Roster</p>
                    <button onClick={() => setActiveTab('shift_roster')} className="mt-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm">+ Manage Shifts</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">

          <div className="bg-white rounded-xl p-6 shadow border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Requests</p>
                <h3 className="text-3xl font-bold text-yellow-600 mt-2">12</h3>
              </div>
              <div className="p-3 rounded-full bg-yellow-100">
                <Clock className="w-6 h-6 text-yellow-700" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Approved Today</p>
                <h3 className="text-3xl font-bold text-green-600 mt-2">8</h3>
              </div>
              <div className="p-3 rounded-full bg-green-100">
                <CheckCircle className="w-6 h-6 text-green-700" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Rejected Today</p>
                <h3 className="text-3xl font-bold text-red-600 mt-2">2</h3>
              </div>
              <div className="p-3 rounded-full bg-red-100">
                <XCircle className="w-6 h-6 text-red-700" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Employees On Leave</p>
                <h3 className="text-3xl font-bold text-blue-600 mt-2">5</h3>
              </div>
              <div className="p-3 rounded-full bg-blue-100">
                <Users className="w-6 h-6 text-blue-700" />
              </div>
            </div>
          </div>

        </div>

        {/* Approval Queue */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-800">
                Pending Leave Approval Queue
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Review and approve employee leave applications
              </p>
            </div>
          </div>

          <div className="overflow-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 text-gray-700 text-sm">
                <tr>
                  <th className="px-4 py-3 text-left">Employee</th>
                  <th className="px-4 py-3 text-left">Department</th>
                  <th className="px-4 py-3 text-left">Leave Type</th>
                  <th className="px-4 py-3 text-left">Dates</th>
                  <th className="px-4 py-3 text-left">Days</th>
                  <th className="px-4 py-3 text-left">Reason</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {leaveRequests.map((leave) => (
                  <tr key={leave.id} className="hover:bg-gray-50">

                    <td className="px-4 py-4">
                      <div>
                        <p className="font-semibold text-gray-800">
                          {leave.employee}
                        </p>
                        <p className="text-sm text-gray-500">
                          {leave.empCode}
                        </p>
                      </div>
                    </td>

                    <td className="px-4 py-4 text-sm text-gray-700">
                      {leave.department}
                    </td>

                    <td className="px-4 py-4 text-sm text-gray-700">
                      {leave.leaveType}
                    </td>

                    <td className="px-4 py-4 text-sm text-gray-700">
                      {leave.from} → {leave.to}
                    </td>

                    <td className="px-4 py-4 text-sm font-medium">
                      {leave.days}
                    </td>

                    <td className="px-4 py-4 text-sm text-gray-700">
                      {leave.reason}
                    </td>

                    <td className="px-4 py-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold
                      ${leave.status === 'Approved'
                            ? 'bg-green-100 text-green-700'
                            : leave.status === 'Rejected'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}
                      >
                        {leave.status}
                      </span>
                    </td>

                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center gap-2">

                        <button className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm">
                          Approve
                        </button>

                        <button className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm">
                          Reject
                        </button>

                      </div>
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Employees On Leave + Leave Balance */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

          {/* Employees On Leave */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-5">
              Employees On Leave Today
            </h3>

            <div className="space-y-4">
              {employeesOnLeave.map((employee, index) => (
                <div
                  key={index}
                  className="p-4 border rounded-lg flex items-center justify-between"
                >
                  <div>
                    <p className="font-semibold text-gray-800">
                      {employee.employee}
                    </p>
                    <p className="text-sm text-gray-500">
                      {employee.department}
                    </p>
                  </div>

                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                    {employee.leaveType}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Leave Balances */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-5">
              Employee Leave Balances
            </h3>

            <div className="space-y-4">
              {leaveBalances.map((employee, index) => (
                <div
                  key={index}
                  className="border rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-800">
                      {employee.employee}
                    </h4>

                    <button className="text-sm text-blue-600 hover:text-blue-800">
                      Adjust Balance
                    </button>
                  </div>

                  <div className="grid grid-cols-3 gap-3">

                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500">CL</p>
                      <p className="text-xl font-bold text-blue-700">
                        {employee.cl}
                      </p>
                    </div>

                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500">SL</p>
                      <p className="text-xl font-bold text-green-700">
                        {employee.sl}
                      </p>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500">EL</p>
                      <p className="text-xl font-bold text-purple-700">
                        {employee.el}
                      </p>
                    </div>

                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

      </div>
    );
  };

  // Render content based on active tab
  const renderContent = () => {
    const isTabAllowed = tabs.some(tab => tab.id === activeTab);
    if (!isTabAllowed && activeTab !== 'dashboard') {
      return (
        <div className="p-8 text-center bg-white rounded-lg shadow-sm border border-red-100 max-w-lg mx-auto my-12">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600">You do not have the required permissions to access this section.</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'employee-management':
        return <EmployeeManagement />;
      case 'reports':
        return <Reports />;
      case 'loans-advances':
        return <LoansAdvancesManagement />;
      case 'payroll':
        return <PayrollManagement />;
      case 'pension-payroll':
        return <PensionPayrollManagement />;
      case 'it-configuration':
        return <IncomeTaxConfiguration />;
      case 'payroll-governance':
        return <PayrollGovernanceCenter />;
      case 'increments':
        return <PromotionIncrementManagement />;
      case 'suspensions':
        return <SuspensionManagement />;
      case 'arrears':
        return <ArrearManagement initialTab="da-arrears" />;
      case 'attendance':
        return <AttendanceManagement />;
      case 'shift_roster':
        return <ShiftRoster />;
      case 'leave':
        return <LeaveManagement />;
      case 'reimbursements':
        return <ReimbursementReviewPage />;
      case 'provident-fund':
        return <ProvidentFund />;
      case 'festival-advance':
        return <FestivalAdvanceManagement />;
      case 'bank-loan':
        return <BankLoanManagement />;
      case 'lic':
        return <LICManagement />;
      case 'demo-arrear':
        return <ArrearManagement initialTab="da-arrears" />;
      default:
        return renderDashboardContent();
    }
  };

  // Extract current dashboard content into a separate function
  const renderDashboardContent = () => (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.8, delay: 0.2 }}
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8"
    >
      {/* Welcome Section */}
      <motion.section
        initial={{ opacity: 0, scale: 0.95 }}
        animate={isLoaded ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-xl shadow-xl p-8 text-white mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center p-2">
                <img
                  src="/benfish-logo.png"
                  alt="Hospital Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Welcome back, {user?.employee_name || 'Admin'}!</h1>
                <p className="text-primary-100 text-lg mt-1">Here's what's happening in your organization today</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-primary-200">Today's Date</p>
              <p className="text-xl font-semibold">{formatDate(new Date())}</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Key Metrics */}
      <section>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          <StatCard
            icon={Users}
            title="Total Employees"
            value={metrics.totalEmployees}
            subtitle="Active employees"
            color="bg-blue-600"
            onClick={() => setActiveTab('employee-management')}
          />
          <StatCard
            icon={IndianRupee}
            title="Previous Month Net Pay"
            value={`₹${(metrics.previousMonthNetPay / 100000).toFixed(1)}L`}
            subtitle={metrics.latestPayrollMonth || "No payroll data"}
            color="bg-green-600"
            onClick={() => setActiveTab('payroll')}
          />
          <StatCard
            icon={FileText}
            title="Approved Payrolls"
            value={metrics.approvedPayrolls}
            subtitle={metrics.latestPayrollMonth ? `Latest: ${metrics.latestPayrollMonth}` : 'No payrolls yet'}
            color="bg-purple-600"
            onClick={() => setActiveTab('payroll')}
          />
        </motion.div>
      </section>

      <section>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.45 }}
          className="space-y-4 mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Pensioner Snapshot</h3>
              <p className="text-sm text-gray-500">Live counts from pensioner profile records</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
            <StatCard
              icon={Users}
              title="Total Pensioners"
              value={pensionerStats.total_pensioners}
              color="bg-slate-700"
            />
            <StatCard
              icon={CheckCircle}
              title="Active Pensioners"
              value={pensionerStats.active_pensioners}
              color="bg-green-600"
            />
            <StatCard
              icon={XCircle}
              title="Stopped Pensioners"
              value={pensionerStats.stopped_pensioners}
              color="bg-red-600"
            />
            <StatCard
              icon={IndianRupee}
              title="Family Pension"
              value={pensionerStats.family_pension}
              color="bg-amber-600"
            />
            <StatCard
              icon={Calendar}
              title="Retiring This Month"
              value={pensionerStats.retiring_this_month}
              color="bg-blue-600"
            />
          </div>
        </motion.div>
      </section>

      {/* Quick Actions */}
      <section>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="bg-white rounded-xl shadow-lg border border-gray-100 p-6"
        >
          <div className="flex items-center mb-6">
            <Target className="w-6 h-6 text-primary-600 mr-2" />
            <h3 className="text-xl font-semibold text-gray-900">Quick Actions</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* <QuickActionButton
              icon={CreditCard}
              label="Approve Loans"
              onClick={() => setActiveTab('loans-advances')}
              color="orange"
            /> */}
            <QuickActionButton
              icon={IndianRupee}
              label="Process Payroll"
              onClick={() => setActiveTab('payroll')}
              color="green"
            />
            <QuickActionButton
              icon={Users}
              label="Manage Employees"
              onClick={() => setActiveTab('employee-management')}
              color="blue"
            />
            <QuickActionButton
              icon={BarChart3}
              label="View Reports"
              onClick={() => setActiveTab('reports')}
              color="purple"
            />
          </div>
        </motion.div>
      </section>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <motion.header
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="bg-white shadow-lg border-b border-gray-200 sticky top-0 z-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <img
                src="/benfish-logo.png"
                alt="Hospital Logo"
                className="w-10 h-10 object-contain mr-3"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900 text-shadow">
                  Pune Cantonment Board
                </h1>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-gray-600">
                    Dexpert HRMS
                  </p>
                  <img
                    src="/wb-govt-emblem.png"
                    alt="Pune Cantonment Board"
                    className="w-5 h-5 object-contain"
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {/* <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <Search className="w-5 h-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors relative"
              >
                <Bell className="w-5 h-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <Settings className="w-5 h-5" />
              </motion.button> */}

              {/* User Profile Dropdown */}
              <div className="relative user-menu">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 p-2 text-gray-700 hover:text-gray-900 transition-colors"
                >
                  <User className="w-5 h-5" />
                  <span className="text-sm font-medium">{user?.employee_name || 'User'}</span>
                </motion.button>

                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                  >
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{user?.employee_name}</p>
                      <p className="text-xs text-gray-500">{user?.role_display_name || user?.role_name}</p>
                    </div>
                    <motion.button
                      whileHover={{ backgroundColor: '#f3f4f6' }}
                      onClick={logout}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </motion.button>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Navigation Tabs */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-6 overflow-x-auto whitespace-nowrap">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderContent()}
      </main>
    </div>
  );
};

export default Dashboard;
